import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

type HelpCenterPdfScrollViewerProps = {
  pdfBlob: Blob | null;
  loading?: boolean;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
};

function isRenderCancelled(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: string }).name === "RenderingCancelledException"
  );
}

async function renderPageToCanvas(
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  containerWidth: number,
  activeTasks: RenderTask[],
): Promise<void> {
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to acquire canvas context.");
  }

  const pixelRatio = window.devicePixelRatio || 1;
  const baseViewport = page.getViewport({ scale: 1, rotation: page.rotate });
  const displayScale = Math.min(2, Math.max(0.5, containerWidth / baseViewport.width));
  const renderViewport = page.getViewport({
    scale: displayScale * pixelRatio,
    rotation: page.rotate,
  });

  canvas.width = renderViewport.width;
  canvas.height = renderViewport.height;
  canvas.style.width = `${renderViewport.width / pixelRatio}px`;
  canvas.style.height = `${renderViewport.height / pixelRatio}px`;

  context.clearRect(0, 0, canvas.width, canvas.height);

  const renderTask = page.render({
    canvasContext: context,
    viewport: renderViewport,
  });
  activeTasks.push(renderTask);

  await renderTask.promise;
}

export default function HelpCenterPdfScrollViewer({
  pdfBlob,
  loading = false,
  loadingMessage = "Loading PDF instructions...",
  errorMessage = "Unable to render PDF instructions.",
  emptyMessage = "No PDF instructions available.",
}: HelpCenterPdfScrollViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const renderVersionRef = useRef(0);
  const activeRenderTasksRef = useRef<RenderTask[]>([]);
  const lastContainerWidthRef = useRef(0);

  const [parsing, setParsing] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [firstPageReady, setFirstPageReady] = useState(false);
  const [loadingMorePages, setLoadingMorePages] = useState(false);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(0);

  const cancelActiveRenderTasks = useCallback(() => {
    for (const task of activeRenderTasksRef.current) {
      task.cancel();
    }
    activeRenderTasksRef.current = [];
  }, []);

  const renderAllPages = useCallback(async () => {
    const container = pagesRef.current;
    const scrollContainer = scrollRef.current;
    const pdf = pdfRef.current;
    if (!container || !scrollContainer || !pdf) {
      return;
    }

    cancelActiveRenderTasks();
    const renderVersion = ++renderVersionRef.current;
    setRendering(true);
    setFirstPageReady(false);
    setLoadingMorePages(false);

    try {
      container.replaceChildren();
      const containerWidth = Math.max(320, scrollContainer.clientWidth - 48);
      lastContainerWidthRef.current = containerWidth;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        if (renderVersion !== renderVersionRef.current) {
          return;
        }

        const page = await pdf.getPage(pageNum);
        const canvas = document.createElement("canvas");
        canvas.className = "help-center-pdf-page-canvas";
        canvas.setAttribute("data-page", String(pageNum));
        canvas.setAttribute("aria-label", `Page ${pageNum}`);

        const pageShell = document.createElement("div");
        pageShell.className = "help-center-pdf-page-shell";
        pageShell.appendChild(canvas);
        container.appendChild(pageShell);

        try {
          await renderPageToCanvas(
            page,
            canvas,
            containerWidth,
            activeRenderTasksRef.current,
          );
        } catch (renderError) {
          if (isRenderCancelled(renderError)) {
            return;
          }
          throw renderError;
        }

        if (renderVersion !== renderVersionRef.current) {
          return;
        }

        if (pageNum === 1) {
          setFirstPageReady(true);
          setRendering(false);
          if (pdf.numPages > 1) {
            setLoadingMorePages(true);
          }
        }
      }
    } catch {
      if (renderVersion === renderVersionRef.current) {
        setError(errorMessage);
        container.replaceChildren();
        setFirstPageReady(false);
      }
    } finally {
      if (renderVersion === renderVersionRef.current) {
        activeRenderTasksRef.current = [];
        setRendering(false);
        setLoadingMorePages(false);
      }
    }
  }, [cancelActiveRenderTasks, errorMessage]);

  useEffect(() => {
    if (!pdfBlob) {
      cancelActiveRenderTasks();
      pdfRef.current = null;
      setTotalPages(0);
      setFirstPageReady(false);
      setLoadingMorePages(false);
      setError("");
      pagesRef.current?.replaceChildren();
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setParsing(true);
      setError("");

      try {
        const data = await pdfBlob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          void pdf.destroy();
          return;
        }

        if (pdfRef.current) {
          void pdfRef.current.destroy();
        }

        pdfRef.current = pdf;
        setTotalPages(pdf.numPages);
      } catch {
        if (!cancelled) {
          setError(errorMessage);
          pdfRef.current = null;
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) {
          setParsing(false);
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      cancelActiveRenderTasks();
      if (pdfRef.current) {
        void pdfRef.current.destroy();
        pdfRef.current = null;
      }
    };
  }, [pdfBlob, cancelActiveRenderTasks, errorMessage]);

  useEffect(() => {
    if (!pdfRef.current || totalPages === 0) {
      return;
    }

    void renderAllPages();
  }, [totalPages, renderAllPages]);

  useEffect(() => {
    const handleWindowResize = () => {
      const scrollContainer = scrollRef.current;
      if (!scrollContainer || totalPages === 0) {
        return;
      }

      const nextWidth = Math.max(320, scrollContainer.clientWidth - 48);
      if (Math.abs(nextWidth - lastContainerWidthRef.current) < 24) {
        return;
      }

      void renderAllPages();
    };

    let resizeTimer: number | undefined;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(handleWindowResize, 250);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(resizeTimer);
    };
  }, [totalPages, renderAllPages]);

  useEffect(() => {
    const blockShortcuts = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        ["s", "p", "c", "a"].includes(event.key.toLowerCase())
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", blockShortcuts);
    return () => document.removeEventListener("keydown", blockShortcuts);
  }, []);

  const showLoading =
    loading || parsing || (totalPages > 0 && !firstPageReady && rendering);

  if (!pdfBlob && !showLoading) {
    return (
      <div className="help-center-pdf-scroll-viewer-empty">{emptyMessage}</div>
    );
  }

  if (error) {
    return <div className="help-center-pdf-scroll-viewer-error">{error}</div>;
  }

  return (
    <div className="help-center-pdf-scroll-viewer">
      <div
        ref={scrollRef}
        className="help-center-pdf-scroll-viewer-pages-wrap"
        onContextMenu={(event) => event.preventDefault()}
      >
        {showLoading ? (
          <div className="help-center-pdf-scroll-viewer-loading">
            <span className="secure-pdf-viewer-spinner" aria-hidden="true" />
            {loadingMessage}
          </div>
        ) : null}
        <div ref={pagesRef} className="help-center-pdf-scroll-viewer-pages" />
      </div>

      {totalPages > 0 ? (
        <div className="help-center-pdf-scroll-viewer-footer">
          <span>
            {totalPages} {totalPages === 1 ? "page" : "pages"}
          </span>
          <span className="help-center-pdf-scroll-viewer-footer-hint">
            {loadingMorePages ? "Loading remaining pages…" : "Scroll to read"}
          </span>
        </div>
      ) : null}
    </div>
  );
}
